import { Box } from "@mui/material";
import React from "react";
import { Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import CustomLinearProgress from "../CustomLinearProgress";
import { ButtonNextStep, ButtonPrevStep } from "../buttons/ButtonNavigation";
import { CloseModalIcon } from "../buttons/IconButtons";

/**
 * @Email : azapata@ibl.mx
 * @Author: Ferro Zapata Arnulfo
 * @jobTittle: Full Stack Developer
 *
 * CustomModal component renders a modal dialog with customizable header,
 * progress bar, body content, and footer with navigation buttons.
 *
 * @param {Object} props - Component properties.
 * @param {string} props.title - Translation key for the modal title.
 * @param {React.ReactNode} props.rightTitleButton - Optional element to render on the right side of the modal title.
 * @param {React.ReactNode} props.children - Content to be rendered in the modal body.
 * @param {string} [props.size="lg"] - Bootstrap modal size (e.g., "sm", "md", "lg", "xl").
 * @param {React.ReactNode|null} [props.TablePagination=null] - Optional pagination element to display in the footer.
 * @param {string} [props.buttonConfirm="confirm"] - Translation key for the confirm button text.
 * @param {string} [props.buttonCancelText="dismiss"] - Translation key for the cancel button text.
 * @param {boolean} [props.cancelButton=true] - Flag to display the cancel button.
 * @param {boolean} [props.confirmButton=true] - Flag to display the confirm button.
 * @param {boolean} [props.closeButton=true] - Flag to display the close (X) button.
 * @param {string} [props.display=""] - Additional display classes for the modal container.
 * @param {boolean} [props.showProgress=false] - Flag to display the progress bar.
 * @param {number} [props.progress=0] - Progress value for the linear progress bar (0–100).
 * @param {boolean} [props.disabled=false] - Disables the confirm button when set to true.
 * @param {Function|boolean} [props.onClose=false] - Optional callback triggered when the close (X) button is clicked. If not provided, `onHide` is used.
 * @param {Function} props.onHide - Callback invoked when the modal is closed (via cancel button or backdrop).
 * @param {string} [props.confirmClass="btn-next-step"] - CSS class name for styling the confirm button.
 * @param {Function} props.onConfirm - Callback invoked when the confirm button is clicked.
 * @param {JSX.Element} props.topLeftHeader - Element to display in the top-left corner of the modal (e.g., a title or custom header).
 * @param {string} [props.cancelClass=""] - CSS class name for styling the cancel button.
 * @param {string} [props.classNameBody =""] - CSS class name for styling the body.
 * @param {string} [props.scroll =true] - Enable (true) / Disable (false) scroll.
 * @returns {JSX.Element} Custom modal component.
 */

const CustomModal = (props) => {
    const { t } = useTranslation();

    const {
        title,
        rightTitleButton,
        children,
        size = "lg",
        TablePagination = null,
        buttonConfirm = "confirm",
        buttonCancelText = "dismiss",
        cancelButton = true,
        confirmButton = true,
        closeButton = true,
        display = "",
        showProgress = false,
        progress = 0,
        disabled = false,
        onClose = false,
        onHide,
        confirmClass = 'btn-next-step',
        onConfirm,
        topLeftHeader,
        cancelClass = "",
        classNameBody = "",
        scroll=true,
        show,
    } = props;

    return (
        <Modal
            show={show}
            onHide={onHide}
            backdrop={display === "d-none" ? false : "static"}
            size={size}
            centered
            className={`modal-container ${display}`}
            title={""}
        >
            <div className={`m-0 p-0`}>
                <div className={`${topLeftHeader ? "justify-content-between" : "justify-content-end"} d-flex w-100`}>
                    {topLeftHeader && <div className={"w-100"}>{topLeftHeader}</div>}
                    {closeButton && <div className={"btn-icon-cancel ml-5 position-absolute position-absolute-top-right"}><CloseModalIcon onClick={onClose ? onClose : onHide} /></div>}
                </div>
            </div>

            {(title || rightTitleButton) &&
                <Modal.Header className="col-12">
                    <Modal.Title id={`m_cm_tittle_${title}`} className="row col-12 modal-title d-flex align-content-center justify-content-center">
                        {t(title)}
                        {rightTitleButton}
                    </Modal.Title>
                </Modal.Header>
            }

            {showProgress && (
                <div className="col-12 pt-2 h-9px">
                    <CustomLinearProgress progress={progress} show={showProgress} modal={true} />
                </div>
            )}

            <Modal.Body className={`${classNameBody} ${scroll ? ' overflow-y-auto': ''}`} id={`m_cm_body_${title}`}>{children}</Modal.Body>

            {(cancelButton || confirmButton || TablePagination) &&
                <>
                    <Modal.Footer id={`m_cm_footer_${title}`} className={"pt-4"}>
                        {TablePagination && <div className="justify-content-center">{TablePagination}</div>}

                        {(cancelButton || confirmButton) && (
                            <div className="d-content row">
                                {cancelButton && (
                                    <Box className={`p-0  ${confirmButton ? "col-sm-6" : "col-sm-12"}`}>
                                        <ButtonPrevStep
                                            id="btn-cm-cancel"
                                            text={buttonCancelText}
                                            click={() => onHide()}
                                            uppercase={true}
                                            classNameBtn={cancelClass}
                                        />
                                    </Box>
                                )}
                                {confirmButton && (
                                    <Box className={`p-0 ${!cancelButton && confirmButton ? 'col-sm-12' : confirmButton ? "col-sm-6" : "col-sm-12"}`}>
                                        <ButtonNextStep
                                            id="btn-cm-confirm"
                                            text={buttonConfirm}
                                            click={() => onConfirm()}
                                            disabled={disabled}
                                            className={confirmClass}
                                            uppercase={true}
                                        />
                                    </Box>
                                )}
                            </div>
                        )}
                    </Modal.Footer>
                </>
            }
        </Modal>
    );
};

export default CustomModal;
